import { forwardRef, useRef, useState } from "react";
import RcPicker, { RangePicker as RcRangePicker } from "rc-picker";
import cx from "classnames";
import Flex from "../flex";
import Box from "../box";
import Button from "../button";
import Tag from "../tag";
import { CalendarOutlined, ClockCircleOutlined, CloseCircleFilled } from "../../icons";
import { useControllableState } from "../../hooks";
import { compose } from "../../utils/func-util";
import locale from "./locale";
import { getPlaceholder, getRangePlaceholder, getTimeProps, findPresetRange } from "./util";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Components = {
  button: function PickerButton(props) {
    return /*#__PURE__*/_jsx(Button, {
      size: "small",
      type: "primary",
      ...props
    });
  },
  rangeItem: function PickerTag(props) {
    return /*#__PURE__*/_jsx(Tag, {
      color: "blue",
      ...props
    });
  }
};
function generateSinglePicker(generateConfig, picker, displayName) {
  const SinglePicker = /*#__PURE__*/forwardRef((props, ref) => {
    const {
      format,
      showTime
    } = props;
    const {
      className,
      size,
      bordered = true,
      alignSider = "left",
      renderSider,
      panelRender: customPanelRender,
      placeholder,
      ...restProps
    } = props;
    const prefixCls = "ant-picker";
    const classString = cx(className, {
      [`${prefixCls}-${size}`]: size,
      [`${prefixCls}-borderless`]: !bordered
    });
    const additionalProps = {
      showToday: true
    };
    let additionalOverrideProps = {};
    if (picker) {
      additionalOverrideProps.picker = picker;
    }
    const mergedPicker = picker || props.picker;
    additionalOverrideProps = {
      ...additionalOverrideProps,
      ...(showTime ? getTimeProps({
        format,
        picker: mergedPicker,
        ...showTime
      }) : {}),
      ...(mergedPicker === "time" ? getTimeProps({
        format,
        ...props,
        picker: mergedPicker
      }) : {})
    };
    const panelRenders = [];
    if (renderSider) {
      panelRenders.push(node => /*#__PURE__*/_jsxs(Flex, {
        inline: true,
        flexDirection: alignSider === "left" ? "row" : "row-reverse",
        children: [renderSider && /*#__PURE__*/_jsx(Box, {
          p: 2,
          borderLeft: alignSider === "left" ? "none" : "popover",
          borderRight: alignSider === "left" ? "popover" : "none",
          children: renderSider()
        }), /*#__PURE__*/_jsx("div", {
          children: node
        })]
      }));
    }
    if (customPanelRender) {
      panelRenders.push(customPanelRender);
    }
    const panelRender = panelRenders.length > 0 ? compose(...panelRenders) : null;
    const clearIcon = /*#__PURE__*/_jsx(CloseCircleFilled, {});
    const suffixIcon = mergedPicker === "time" ? /*#__PURE__*/_jsx(ClockCircleOutlined, {}) : /*#__PURE__*/_jsx(CalendarOutlined, {});
    return /*#__PURE__*/_jsx(RcPicker, {
      ref: ref,
      placeholder: getPlaceholder(mergedPicker, locale, placeholder),
      clearIcon: clearIcon,
      suffixIcon: suffixIcon,
      allowClear: true,
      transitionName: "ant-slide-up",
      ...additionalProps,
      ...restProps,
      ...additionalOverrideProps,
      locale: locale.lang,
      className: classString,
      prefixCls: prefixCls,
      panelRender: panelRender,
      generateConfig: generateConfig,
      prevIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-prev-icon`
      }),
      nextIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-next-icon`
      }),
      superPrevIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-super-prev-icon`
      }),
      superNextIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-super-next-icon`
      }),
      components: Components
    });
  });
  if (displayName) {
    SinglePicker.displayName = displayName;
  }
  return SinglePicker;
}
function generateRangePicker(generateConfig) {
  const RangePicker = /*#__PURE__*/forwardRef((props, ref) => {
    const {
      format,
      showTime,
      picker
    } = props;
    const {
      className,
      size,
      bordered = true,
      renderSider,
      alignSider = "left",
      panelRender: customPanelRender,
      renderExtraFooter: customFooterRender,
      placeholder,
      ranges,
      value,
      defaultValue,
      onChange,
      open,
      onOpenChange,
      ...restProps
    } = props;
    const [pendingDates, setPendingDates] = useState(null);
    const [dates, setDates] = useControllableState(value, onChange, defaultValue);
    const [isOpen, setIsOpen] = useControllableState(open, onOpenChange, false);
    const lastChangeTime = useRef();
    const applyButtonRef = useRef();
    const displayDates = showTime ? dates : pendingDates ?? dates;
    const currentRange = findPresetRange(displayDates, ranges);
    const prefixCls = "ant-picker";
    const classString = cx(className, {
      [`${prefixCls}-${size}`]: size,
      [`${prefixCls}-borderless`]: !bordered
    });
    const hasTimePicker = showTime || picker === "time";
    const additionalOverrideProps = {
      ...(showTime ? getTimeProps({
        format,
        picker,
        ...showTime
      }) : {}),
      ...(picker === "time" ? getTimeProps({
        format,
        ...props,
        picker
      }) : {})
    };
    const handleChange = val => {
      lastChangeTime.current = Date.now();
      if (!isOpen || hasTimePicker) {
        setDates(val);
      } else {
        setPendingDates(val);
        setTimeout(() => applyButtonRef.current?.focus());
      }
    };
    const handleOpenChange = open => {
      const allowChange = Date.now() - (lastChangeTime.current ?? 0) > 100;
      if (hasTimePicker || allowChange) {
        setIsOpen(open);
      }
      if (!hasTimePicker && allowChange && !open && pendingDates) {
        setDates(pendingDates);
        setPendingDates(null);
      }
    };
    const handleApply = () => {
      setDates(pendingDates);
      setPendingDates(null);
      setIsOpen(false);
    };
    const handleCancel = () => {
      setPendingDates(null);
      setIsOpen(false);
    };
    const handlePresetSelect = range => {
      setDates(range);
      setPendingDates(null);
      setIsOpen(false);
    };
    const renderExtraFooter = hasTimePicker ? customFooterRender : mode => /*#__PURE__*/_jsxs(_Fragment, {
      children: [customFooterRender?.(mode), /*#__PURE__*/_jsx(ActionFooter, {
        flexDirection: alignSider === "left" ? "row" : "row-reverse",
        okButtonProps: {
          ref: applyButtonRef,
          disabled: !pendingDates
        },
        onOk: handleApply,
        onCancel: handleCancel
      })]
    });
    const panelRenders = [];
    if (renderSider || ranges) {
      panelRenders.push(node => /*#__PURE__*/_jsxs(Flex, {
        inline: true,
        flexDirection: alignSider === "left" ? "row" : "row-reverse",
        children: [renderSider && /*#__PURE__*/_jsx(Box, {
          p: 2,
          borderLeft: alignSider === "left" ? "none" : "popover",
          borderRight: alignSider === "left" ? "popover" : "none",
          children: renderSider()
        }), ranges && /*#__PURE__*/_jsx(PresetRangeList, {
          minWidth: 120,
          borderLeft: alignSider === "left" ? "none" : "popover",
          borderRight: alignSider === "left" ? "popover" : "none",
          ranges: ranges,
          currentRange: currentRange,
          onChange: handlePresetSelect
        }), /*#__PURE__*/_jsx("div", {
          children: node
        })]
      }));
    }
    if (customPanelRender) {
      panelRenders.push(customPanelRender);
    }
    const panelRender = panelRenders.length > 0 ? compose(...panelRenders) : null;
    const separator = /*#__PURE__*/_jsx("span", {
      "aria-label": "to",
      className: `${prefixCls}-separator`,
      children: "→"
    });
    const clearIcon = /*#__PURE__*/_jsx(CloseCircleFilled, {});
    const suffixIcon = picker === "time" ? /*#__PURE__*/_jsx(ClockCircleOutlined, {}) : /*#__PURE__*/_jsx(CalendarOutlined, {});
    return /*#__PURE__*/_jsx(RcRangePicker, {
      ref: ref,
      placeholder: getRangePlaceholder(picker, locale, placeholder),
      separator: separator,
      clearIcon: clearIcon,
      suffixIcon: suffixIcon,
      allowClear: true,
      transitionName: "ant-slide-up",
      ...restProps,
      ...additionalOverrideProps,
      className: classString,
      locale: locale.lang,
      prefixCls: prefixCls,
      value: displayDates,
      onChange: handleChange,
      open: isOpen,
      onOpenChange: handleOpenChange,
      panelRender: panelRender,
      renderExtraFooter: renderExtraFooter,
      generateConfig: generateConfig,
      prevIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-prev-icon`
      }),
      nextIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-next-icon`
      }),
      superPrevIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-super-prev-icon`
      }),
      superNextIcon: /*#__PURE__*/_jsx("span", {
        className: `${prefixCls}-super-next-icon`
      }),
      components: Components,
      ranges: undefined
    });
  });
  return RangePicker;
}
function ActionFooter(_ref) {
  let {
    onOk,
    onCancel,
    okButtonProps,
    cancelButtonProps,
    color,
    ...rest
  } = _ref;
  return /*#__PURE__*/_jsxs(Flex, {
    gap: 2,
    p: 2,
    color: color,
    ...rest,
    children: [/*#__PURE__*/_jsx(Button, {
      size: "small",
      type: "primary",
      ...okButtonProps,
      onClick: onOk,
      children: "Apply"
    }), /*#__PURE__*/_jsx(Button, {
      size: "small",
      ...cancelButtonProps,
      onClick: onCancel,
      children: "Cancel"
    })]
  });
}
function PresetRangeList(_ref2) {
  let {
    ranges,
    currentRange,
    onChange,
    color,
    ...rest
  } = _ref2;
  return /*#__PURE__*/_jsxs(Flex, {
    gap: 2,
    p: 2,
    flexDirection: "column",
    color: color,
    ...rest,
    children: [/*#__PURE__*/_jsx(Tag, {
      color: currentRange ? "default" : "processing",
      textAlign: "center",
      cursor: "pointer",
      children: "Custom"
    }), Object.entries(ranges ?? {}).map(_ref3 => {
      let [name, range] = _ref3;
      return /*#__PURE__*/_jsx(Tag, {
        color: name === currentRange ? "processing" : "default",
        onClick: () => onChange?.(range),
        textAlign: "center",
        cursor: "pointer",
        children: name
      }, name);
    })]
  });
}
function generatePicker(config) {
  const DatePicker = generateSinglePicker(config);
  const WeekPicker = generateSinglePicker(config, "week", "WeekPicker");
  const MonthPicker = generateSinglePicker(config, "month", "MonthPicker");
  const YearPicker = generateSinglePicker(config, "year", "YearPicker");
  const TimePicker = generateSinglePicker(config, "time", "TimePicker");
  const QuarterPicker = generateSinglePicker(config, "quarter", "QuarterPicker");
  const RangePicker = generateRangePicker(config);
  return Object.assign(DatePicker, {
    WeekPicker,
    MonthPicker,
    YearPicker,
    TimePicker,
    QuarterPicker,
    RangePicker
  });
}
export default generatePicker;